import React, { forwardRef, useImperativeHandle, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import { Paper, Typography, Collapse, Link, Divider } from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from "@material-ui/icons";

import HorizontalAssetList from "../../Shared/AssetPreviewList/HorizontalAssetList";
import { getAdGroupLabel } from "../../../utils/creativeMatching";

// Review for Smart Traffic, which is the one flow that submits several ads at
// once — routinely 60+ placements and 15+ creatives. The other steppers hand
// AssetReview a flat list because they build a single ad, where a flat list
// is the whole truth. Here it hides the only thing worth confirming: which
// creatives end up on which placements, under which settings.
//
// Grouped by ad, because an ad is the unit that gets created in CM360 and the
// unit whose settings apply. Placements collapse behind a count — at 60+ they
// would bury the creatives, but they are still the thing a user goes looking
// for when a number looks wrong.

const useStyles = makeStyles((theme) => ({
  h6: {
    fontWeight: 400,
    fontSize: "1.15rem"
  },
  summary: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  adPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  adHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    flexWrap: "wrap"
  },
  adTitle: {
    fontWeight: 500
  },
  adCounts: {
    color: theme.palette.text.secondary
  },
  settingsLine: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  customBadge: {
    color: theme.palette.primary.main,
    fontWeight: 500
  },
  divider: {
    margin: `${theme.spacing(1.5)}px 0`
  },
  placementToggle: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
    marginTop: theme.spacing(1)
  },
  toggleIcon: {
    fontSize: "1.1rem",
    marginRight: theme.spacing(0.5)
  },
  placementList: {
    margin: `${theme.spacing(1)}px 0 0 0`,
    paddingLeft: theme.spacing(3),
    color: theme.palette.text.secondary,
    maxHeight: 260,
    overflowY: "auto"
  },
  placementItem: {
    wordBreak: "break-all",
    marginBottom: 2
  },
  empty: {
    color: theme.palette.text.secondary,
    fontStyle: "italic"
  }
}));

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const ROTATION_LABELS = {
  WEIGHTED: "Weighted",
  SEQUENTIAL: "Sequential",
  CLICK_THROUGH_RATE: "Click-through rate"
};

const formatDate = (value) => {
  if (!value) return null;
  // Dates live in the creatives slice as dayjs objects.
  if (typeof value.format === "function") {
    return value.isValid && !value.isValid() ? null : value.format("MMM D, YYYY");
  }
  return String(value);
};

export const formatAdSettings = (settings, customized) => {
  if (!settings) return null;

  const parts = [];
  parts.push(ROTATION_LABELS[settings.rotationType] || settings.rotationType);

  const start = formatDate(settings.startDate);
  const end = formatDate(settings.endDate);
  if (start && end) parts.push(`${start} – ${end}`);
  else if (start) parts.push(`from ${start}`);

  if (settings.hardCutoff) parts.push("hard cutoff");

  const keys = settings.dynamicTargetingKeys || [];
  if (keys.length) parts.push(plural(keys.length, "targeting key"));

  parts.push(customized ? "own settings" : "shared settings");

  return parts.filter(Boolean).join(" · ");
};

const AdSection = ({ group, index, settings, customized }) => {
  const classes = useStyles();
  const [showPlacements, setShowPlacements] = useState(false);

  const placements = group.placements || [];
  const settingsLine = formatAdSettings(settings, customized);

  return (
    <Paper className={classes.adPaper} variant="outlined">
      <div className={classes.adHeader}>
        <Typography variant="subtitle1" className={classes.adTitle}>
          {`Ad ${index + 1} — ${getAdGroupLabel(group)}`}
        </Typography>
        <Typography variant="body2" className={classes.adCounts}>
          {`${plural(group.creativeIds.length, "creative")} · ${plural(
            placements.length,
            "placement"
          )}`}
        </Typography>
      </div>

      {settingsLine && (
        <Typography variant="caption" display="block" className={classes.settingsLine}>
          {settingsLine}
          {customized && (
            <span className={classes.customBadge}>{"  •  customized"}</span>
          )}
        </Typography>
      )}

      <Divider className={classes.divider} />

      {group.creativeIds.length === 0 ? (
        <Typography variant="body2" className={classes.empty}>
          No creatives assigned to this ad.
        </Typography>
      ) : (
        <HorizontalAssetList
          isClickEnabled={false}
          isDragDisabled={true}
          isThumbnailEnabled={true}
          isWeightEnabled={true}
          selectedIds={[]}
          droppableId={`review-${group.key}`}
          filterFn={(c) => group.creativeIds.includes(c.id)}
        />
      )}

      {placements.length > 0 && (
        <>
          <Link
            component="button"
            variant="body2"
            type="button"
            className={classes.placementToggle}
            onClick={() => setShowPlacements((open) => !open)}
            aria-expanded={showPlacements}
          >
            {showPlacements ? (
              <ExpandLessIcon className={classes.toggleIcon} />
            ) : (
              <ExpandMoreIcon className={classes.toggleIcon} />
            )}
            {showPlacements
              ? "Hide placements"
              : `Show ${plural(placements.length, "placement")}`}
          </Link>

          <Collapse in={showPlacements} unmountOnExit>
            <ul className={classes.placementList}>
              {placements.map((p) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={p.id}
                  className={classes.placementItem}
                >
                  {p.name}
                </Typography>
              ))}
            </ul>
          </Collapse>
        </>
      )}
    </Paper>
  );
};

export default forwardRef(
  ({ adGroups = [], getAdSettings, isAdCustomized }, ref) => {
    const classes = useStyles();

    useImperativeHandle(ref, () => ({
      validate() {
        return { hasErrors: false, message: "" };
      }
    }));

    // A creative matched to two ads is counted once here but shown under
    // both — the number answers "how many did I upload", the sections answer
    // "where does each one go".
    const uniqueCreativeIds = new Set();
    let placementCount = 0;
    for (const group of adGroups) {
      group.creativeIds.forEach((id) => uniqueCreativeIds.add(id));
      placementCount += (group.placements || []).length;
    }

    return (
      <React.Fragment>
        <Typography variant="h6" className={classes.h6}>
          Review and confirm selected placement(s) and creative(s)
        </Typography>

        <Typography variant="body2" className={classes.summary}>
          {`${plural(adGroups.length, "ad")} · ${plural(
            uniqueCreativeIds.size,
            "creative"
          )} · ${plural(placementCount, "placement")}`}
        </Typography>

        {adGroups.map((group, index) => (
          <AdSection
            key={group.key}
            group={group}
            index={index}
            settings={getAdSettings ? getAdSettings(group) : null}
            customized={isAdCustomized ? isAdCustomized(group.key) : false}
          />
        ))}
      </React.Fragment>
    );
  }
);
